import React, { useState, useRef, useEffect, useCallback } from "react";
import { useUserContext } from "../context/UserContext.tsx";
import StoreSelectorTrigger from "./StoreSelector/StoreSelectorTrigger.tsx";
import StoreSelectorPanel from "./StoreSelector/StoreSelectorPanel.tsx";
import { toast } from "./Toast/toast.ts";

export default function GlobalStoresDropDown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    user,
    globalstorevalue,
    setGlobalstorevalue,
    globalStoreData,
    setGlobalStoreData,
    isActiveStoreHttpOnly,
  } = useUserContext();

  const stores: any[] = (user as any)?.stores ?? [];
  const storeCount = stores.length;
  const isAllSelected = globalstorevalue === null;

  // Derive the trigger label from context instead of a separate state
  const selectedStore = stores.find((s) => s._id === globalstorevalue);
  const triggerLabel = isAllSelected
    ? `All Stores Combined (${storeCount})`
    : selectedStore?.name ?? "All Stores Combined";

  // Seed globalStoreData on first load
  useEffect(() => {
    if ((user as any)?.stores && globalStoreData === null) {
      setGlobalStoreData((user as any).stores);
    }
  }, [user, globalStoreData]);

  // Warn when the selected store uses a plain HTTP remote URL (no SSL)
  useEffect(() => {
    if (isActiveStoreHttpOnly && !isAllSelected) {
      toast.warning(
        "This store uses an insecure HTTP connection. Some data may not load due to a missing SSL certificate.",
        { duration: 6000 }
      );
    }
  }, [globalstorevalue]);

  // Close on outside click
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [handleOutsideClick]);

  // Close on Escape
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") setIsOpen(false);
  };

  const handleSelectAll = () => {
    setGlobalstorevalue(null);
    setGlobalStoreData((user as any)?.stores);
    setIsOpen(false);
  };

  const handleSelectStore = (item: any) => {
    setGlobalstorevalue(item._id);
    setGlobalStoreData(item);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onKeyDown={handleKeyDown}
    >
      <StoreSelectorTrigger
        label={triggerLabel}
        isOpen={isOpen}
        onClick={() => setIsOpen((v) => !v)}
      />

      {isOpen && (
        <StoreSelectorPanel
          stores={stores}
          selectedId={globalstorevalue}
          onSelectAll={handleSelectAll}
          onSelectStore={handleSelectStore}
        />
      )}
    </div>
  );
}